import { GameEvent } from "../enums/GameEvent";
import { Page } from "../enums/Page";
import { html } from "../helpers/webComponents";
import { GameEventService, SwitchPageEvent } from "../services/GameEventService";

/** Default page used by the {@link RootComponent} */
export const DefaultPage: Page = Page.Canvas;

/**
 * A "router" Web Component
 *
 * @remarks Will listen for the Switch Page event and render the requested page if possible. Otherwise, switches to a Not Found page.
 */
export class RootComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();

    /** Current active page */
    private _currentPage: Page = DefaultPage;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Switch Page event
        this._gameEventService.addGameEventListener<SwitchPageEvent>(GameEvent.SwitchPage, event => {
            this.handleSwitchPage(event.page);
        });

        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const gameCanvas: Element | null = this.shadowRoot.querySelector("game-canvas");

        // Verwijder game-canvas als de pagina TravelMap of Inventory is
        if (this._currentPage === Page.TravelMap || this._currentPage === Page.Inventory || this._currentPage === Page.Auth || this._currentPage === Page.Result) {
            if (gameCanvas) {
                gameCanvas.remove();
            }
        }
        else {
            if (!gameCanvas) {
                this.shadowRoot.prepend(html`<game-canvas></game-canvas>`); // 👈 Zet bovenaan in de DOM
            }
        }

        let overlayContainer: Element | null = this.shadowRoot.querySelector("#overlay-container");

        if (!overlayContainer) {
            overlayContainer = document.createElement("div");
            overlayContainer.id = "overlay-container";
            this.shadowRoot.append(overlayContainer);
        }

        if (this._currentPage === Page.Canvas) {
            overlayContainer.innerHTML = "";
            return;
        }

        const newPage: HTMLElement = this.renderCurrentPage();
        newPage.style.position = "absolute";
        newPage.style.top = "0";
        newPage.style.left = "0";
        newPage.style.width = "100%";
        newPage.style.height = "100%";
        overlayContainer.innerHTML = "";
        overlayContainer.append(newPage);
    }

    /**
     * Render the current active page
     *
     * @returns HTML element of the current active page
     */
    private renderCurrentPage(): HTMLElement {
        switch (this._currentPage) {
            /// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            case Page.Canvas:
                return html`<game-canvas></game-canvas>`;
            case Page.TravelMap:
                return html`<game-travelmap></game-travelmap>`;
            case Page.ModalPuzzel:
                return html`<game-modal-puzzel></game-modal-puzzel>`;
            case Page.Journal:
                return html`<game-journal></game-journal>`;
            case Page.Inventory:
                return html`<game-inventory></game-inventory>`;
            case Page.Leaderboard:
                return html`<leaderboard-component-modal></leaderboard-component-modal>`;
            case Page.Result:
                return html`<end-result></end-result>`;
            case Page.Auth:
                return html`<auth-component></auth-component>`;
            case Page.UitlogMessage:
                return html`<uitlog-message></uitlog-message>`;
            default:
                return html`<game-notfound .page="${this._currentPage}"></game-notfound>`;
        }
    }

    /**
     * Switch to a different page
     *
     * @param page Page to switch to
     */
    private handleSwitchPage(page: Page): void {
        this._currentPage = page;

        this.render();
    }
}
